import { cn } from '@/lib/utils'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import { CheckIcon } from 'lucide-react'
import * as React from 'react'

const checkboxVariants = cva(
  'peer border-input cursor-pointer bg-quaternary data-[state=checked]:bg-secondary data-[state=checked]:text-white data-[state=checked]:border-secondary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive size-4 shrink-0 border outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: '',
      },
      size: {
        default: 'size-15.5 rounded-xl',
        sm: 'size-4 rounded-sm',
        md: 'size-8 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const checkIconVariants = cva('', {
  variants: {
    size: {
      default: 'size-6',
      sm: 'size-3',
      md: 'size-4',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

interface Props
  extends Omit<
      React.ComponentProps<typeof CheckboxPrimitive.Root>,
      'value' | 'checked' | 'onCheckedChange' | 'onChange'
    >,
    VariantProps<typeof checkboxVariants> {
  value: CheckboxPrimitive.CheckedState
  onChange?(checked: CheckboxPrimitive.CheckedState): void
}

function Checkbox({
  className,

  value,

  variant,
  size,

  onChange,

  ...props
}: Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(checkboxVariants({ variant, size, className }))}
      {...props}
      checked={value}
      onCheckedChange={onChange}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none"
      >
        <CheckIcon className={cn(checkIconVariants({ size }))} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
